import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import RoundRobinStandings from "../CompetitionSystems/RoundRobinStandings/RoundRobinStandings";
import {
    fetchMatches,
    fetchRoundRobinStandings,
    generateFinalMatches,
    getUserData, returnToPhaseGroup,
    saveMatchResult,
    fetchH2H,
    fetchLeagueView,
    fetchTournamentPlanningContext,
    getToken
} from "../../api/api";
import MatchesTable from "../CompetitionSystems/MatchesTable/MatchesTable";
import MatchResultModal from "../CompetitionSystems/MatchResultModal/MatchResultModal";
import CustomModal from "../../UI/Modal/CustomModal";
import styles from './TourCurForm.module.css';
import SingleEliminationBracket from "../CompetitionSystems/SingleEliminationBracket/SingleEliminationBracket";
import BrazilianBracket from "../CompetitionSystems/BrazilianBracket/BrazilianBracket";
import Spinner from "../Spinner/Spinner";
import Podium from "../Podium/Podium";
import H2HModal from "../CompetitionSystems/H2HModal/H2HModal";
import LeagueView from "../LeagueView/LeagueView";
import RecentResultsDrawer from "../LeagueView/RecentResultsDrawer";
import ScheduledMatchesDrawer from "../LeagueView/ScheduledMatchesDrawer";

const TourCurForm = ({tournament, isArchived, onReturn}) => {
    const [standings, setStandings] = useState([]);
    const [leagueRankings, setLeagueRankings] = useState([]);
    const [matches, setMatches] = useState([]);
    const [editingMatchId, setEditingMatchId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [userData, setUserData] = useState({});
    const [showConfirmButton, setShowConfirmButton] = useState(false);
    const [confirmLabel, setConfirmLabel] = useState("");
    const [redirectToLoginAfterModal, setRedirectToLoginAfterModal] = useState(false);
    const navigate = useNavigate();
    const [nameFilter, setNameFilter] = useState('');
    const [currentAction, setCurrentAction] = useState(null);
    const [isH2hModalOpen, setIsH2hModalOpen] = useState(false);
    const [h2hMatchId, setH2hMatchId] = useState(null);
    const [h2hData, setH2hData] = useState([]);
    const [isH2hLoading, setIsH2hLoading] = useState(false);
    const [planningContext, setPlanningContext] = useState(null);
    const [isRecentDrawerOpen, setIsRecentDrawerOpen] = useState(false);
    const [isScheduledDrawerOpen, setIsScheduledDrawerOpen] = useState(false);

    useEffect(() => {
        // Backend sam decyduje, czy przycisk ma się pokazać — front nie odtwarza reguł uprawnień.
        // Bramka na getToken() oszczędza zbędny 401 dla niezalogowanego gościa.
        if (!tournament?.id || tournament.competitionSystem !== 'COMBINED' || !getToken()) return;
        let cancelled = false;
        (async () => {
            const result = await fetchTournamentPlanningContext(tournament.id);
            if (!cancelled && result.success) setPlanningContext(result.data);
        })();
        return () => { cancelled = true; };
    }, [tournament?.id, tournament?.competitionSystem]);

    const competitionSystemMapping = {
        ROUND_ROBIN: 'Każdy z każdym',
        KNOCKOUT: 'Pucharowy',
        BRAZILIAN: 'Brazylijski do dwóch przegranych',
        BRAZILIAN_FAST: 'Brazylijski szybki',
        COMBINED: 'Faza grupowa + faza finałowa',
        GROUP_LEAGUE: 'Liga grupowa'
    };

    useEffect(() => {
        const fetchData = async () => {
            const data = await getUserData();
            setUserData(data);
        };
        fetchData();
    }, []);

    const userRoles = new Set(userData.authorities || []);
    const isUserSuperVisor = userRoles.has("ROLE_SUPERVISOR") || userRoles.has("ROLE_ADMIN");

    const fetchMatchData = async () => {
        const matchesResult = await fetchMatches(tournament.id);
        if (matchesResult.success) {
            setMatches(matchesResult.data);
        } else {
            console.error('Could not fetch matches:', matchesResult.data);
        }
    };

    const fetchStandingsData = async () => {
        const result = await fetchRoundRobinStandings(tournament.id);
        if (result.success) {
            setStandings(result.data);
        } else {
            console.error('Could not fetch standings');
        }
    };

    useEffect(() => {
        loadData();

        const intervalId = setInterval(() => {
            if (!isArchived && !tournament.isLeague) {
                loadData();
            }
        }, 60000);

        return () => clearInterval(intervalId);
    }, [tournament, isArchived]);

    const loadData = async () => {
        setIsLoading(true);
        if (tournament) {
            if (tournament.competitionSystem === 'ROUND_ROBIN' || tournament.competitionSystem === 'COMBINED') {
                await fetchMatchData();
                await fetchStandingsData();
            } else if (tournament.competitionSystem === 'KNOCKOUT' || tournament.competitionSystem === 'BRAZILIAN' || tournament.competitionSystem === 'BRAZILIAN_FAST') {
                await fetchMatchData();
            } else if (tournament.competitionSystem === 'GROUP_LEAGUE' && isArchived) {
                const result = await fetchLeagueView(tournament.id);
                if (result.success && Array.isArray(result.data?.overallRanking)) {
                    setLeagueRankings(result.data.overallRanking);
                }
            }
        }
        if (!isArchived && !tournament.isLeague) {
            await new Promise(resolve => setTimeout(resolve, 2000));
        }
        setIsLoading(false);
    };

    useEffect(() => {
        loadData();
    }, [tournament]);

    const handleEditMatch = (matchId) => {
        setEditingMatchId(matchId);
        const matchToEdit = matches.find(match => match.id === matchId);
        if (matchToEdit) {
            setIsModalOpen(true);
        } else {
            console.error('Match not found!');
        }
    };

    const onSaveResult = async (matchId, scores, walkover, walkoverWinnerId, injuryResignation, injuryResignationParticipantId, clearMatchData) => {
        const matchResult = {
            matchId,
            scores,
            walkover,
            walkoverWinnerId,
            injuryResignation,
            injuryResignationParticipantId,
            clearMatchData
        };

        // await new Promise(resolve => setTimeout(resolve, 3000));

        const response = await saveMatchResult(matchResult);
        if (response.success) {
            setModalMessage(response.data);
            setShowConfirmButton(false);
            setIsModalOpen(false);
            setEditingMatchId(null);
            await fetchMatchData();
            await fetchStandingsData();
            setIsCustomModalOpen(true);
        } else {
            console.error('Failed to enter result of the match:', response.data);
            setModalMessage(response.data || 'error');
            setShowConfirmButton(false);
            setIsModalOpen(false);
            setIsCustomModalOpen(true);
            if (response.sessionExpired) {
                setRedirectToLoginAfterModal(true);
            }
        }
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setEditingMatchId(null);
        setShowConfirmButton(false);
        setConfirmLabel("");
    };

    const currentMatch = matches.find(match => match.id === editingMatchId);

    const handleCloseCustomModal = () => {
        setIsCustomModalOpen(false);
        if (redirectToLoginAfterModal) {
            // Hard redirect — pełne przeładowanie czyści AuthContext (navbar przestaje pokazywać „Wyloguj").
            window.location.href = "/login?from=" + encodeURIComponent(window.location.pathname);
        }
    };

    const filterFinalPhaseMatches = (matches) => {
        return matches.filter(match => match.matchNumber !== null);
    };


    const handleOpenH2H = async (matchId) => {
        setH2hMatchId(matchId);
        setIsH2hLoading(true);
        try {
            const data = await fetchH2H(matchId);
            setH2hData(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error("Błąd pobierania H2H", e);
            setH2hData([]);
        } finally {
            setIsH2hLoading(false);
            setIsH2hModalOpen(true);
        }
    };

    const handleCloseH2H = () => {
        setIsH2hModalOpen(false);
        setH2hMatchId(null);
        setH2hData([]);
    };


    const renderCombinedSystem = () => {
        const groupedStandings = standings
            .filter(standing => standing.group) // Filter out standings without a group
            .reduce((acc, standing) => {
                const group = standing.group;
                if (!acc[group]) acc[group] = [];
                acc[group].push(standing);
                return acc;
            }, {});

        const groupedMatches = matches
            .filter(match => match.group) // Filter out matches without a group
            .reduce((acc, match) => {
                const group = match.group;
                if (!acc[group]) acc[group] = [];
                acc[group].push(match);
                return acc;
            }, {});

        const allGroupMatches = Object.values(groupedMatches).flat();
        const totalGroupMatches = allGroupMatches.length;
        const playedGroupMatches = allGroupMatches.filter(m => m.participantWinnerId !== null).length;
        const groupProgressPct = totalGroupMatches > 0 ? Math.round((playedGroupMatches / totalGroupMatches) * 100) : 0;

        const finalMatches = filterFinalPhaseMatches(matches);
        const totalFinal = finalMatches.length;
        const playedFinal = finalMatches.filter(m => m.participantWinnerId !== null).length;
        const finalPct = totalFinal > 0 ? Math.round((playedFinal / totalFinal) * 100) : 0;

        const getProgressColor = (p) => {
            if (p === 100) return '#2196f3';
            if (p >= 67) return '#4caf50';
            if (p >= 34) return '#ffeb3b';
            return '#f44336';
        };

        return (
            <>
                <h4 className={styles.groupPhaseTitle}>Faza Grupowa</h4>
                {!isArchived && totalGroupMatches > 0 && (
                    <div className={styles.groupPhaseProgress}>
                        <div className={styles.groupPhaseProgressBar}>
                            <div className={styles.groupPhaseProgressFill}
                                 style={{ width: `${groupProgressPct}%`, backgroundColor: getProgressColor(groupProgressPct) }}/>
                        </div>
                        <div className={styles.groupPhaseProgressText}>
                            {groupProgressPct}% rozegranych ({playedGroupMatches}/{totalGroupMatches})
                        </div>
                    </div>
                )}
                <div className={styles.groupsContainer}>
                    {Object.keys(groupedMatches).map(group => (
                        <div key={group} className={styles.group}>
                            <h4>Grupa {group}</h4>
                            {groupedStandings[group] && groupedStandings[group].length > 0 && (
                                <RoundRobinStandings standings={groupedStandings[group]} matches={groupedMatches[group]}
                                                     isArchived={isArchived}/>
                            )}
                            <MatchesTable
                                matches={groupedMatches[group]}
                                onEditMatch={handleEditMatch}
                                isArchived={isArchived}
                                onOpenH2H={handleOpenH2H}
                                isFinal={filterFinalPhaseMatches(matches).length > 0}/>
                        </div>
                    ))}
                </div>
                {!isArchived && isUserSuperVisor && filterFinalPhaseMatches(matches).length === 0 ? (
                    <button onClick={handleGenerateFinalMatches} className={styles.generateButton}>
                        Generuj mecze finałowe
                    </button>
                ) : (
                    !isArchived && isUserSuperVisor && (
                        <button onClick={handleReturnToFaseGroup} className={styles.returnToPhaseGroupButton}>
                            Powrót do fazy grupowej
                        </button>
                    )
                )}
                {finalMatches.length > 0 && (
                    <>
                        <h4 className={styles.finalPhaseTitle}>Faza Finałowa</h4>
                        {!isArchived && totalFinal > 0 && (
                            <div className={styles.groupPhaseProgress}>
                                <div className={styles.groupPhaseProgressBar}>
                                    <div className={styles.groupPhaseProgressFill}
                                         style={{ width: `${finalPct}%`, backgroundColor: getProgressColor(finalPct) }}/>
                                </div>
                                <div className={styles.groupPhaseProgressText}>
                                    {finalPct}% rozegranych ({playedFinal}/{totalFinal})
                                </div>
                            </div>
                        )}
                        <div style={{ overflowX: 'auto' }}>
                            <SingleEliminationBracket matches={finalMatches}
                                                      onEditMatch={handleEditMatch}
                                                      isArchived={isArchived}
                                                      onOpenH2H={handleOpenH2H}
                                                    />
                        </div>
                    </>
                )}
                {currentMatch && (
                    <MatchResultModal
                        isOpen={isModalOpen}
                        onRequestClose={handleModalClose}
                        onSaveResult={onSaveResult}
                        editingMatch={currentMatch}
                        matchId={editingMatchId}
                        participant1Id={currentMatch.participant1Id}
                        participant2Id={currentMatch.participant2Id}
                        participant1FirstName={currentMatch.participant1FirstName}
                        participant1LastName={currentMatch.participant1LastName}
                        participant2FirstName={currentMatch.participant2FirstName}
                        participant2LastName={currentMatch.participant2LastName}
                        participant1PartnerFirstName={currentMatch.participant1PartnerFirstName}
                        participant1PartnerLastName={currentMatch.participant1PartnerLastName}
                        participant2PartnerFirstName={currentMatch.participant2PartnerFirstName}
                        participant2PartnerLastName={currentMatch.participant2PartnerLastName}
                        isThirdSetTiebreak={tournament.isThirdSetTiebreak}
                        matchFormat={tournament.matchFormat}
                        numberOfPointsInSet={tournament.numberOfPointsInSet}
                        isUserSupervision={isUserSuperVisor}
                        showConfirmButton={showConfirmButton}
                    />
                )}
           </>
        );
    };

    const handleConfirm = async () => {
        if (currentAction) {
            await currentAction();
            // setIsCustomModalOpen(false);
        }
    };

    const handleGenerateFinalMatches = async () => {
        setModalMessage("Czy napewno chcesz wygenerować mecze finałowe ? ");
        setConfirmLabel("Tak, generuje");
        setShowConfirmButton(true);
        setIsCustomModalOpen(true);
        setCurrentAction(() => generateFinalMatchesConfirmAction);
    };

    const handleReturnToFaseGroup = () => {
        setModalMessage("Czy napewno chcesz powrócić do fazy grupowej?");
        setConfirmLabel("Tak, wróć");
        setShowConfirmButton(true);
        setIsCustomModalOpen(true);
        setCurrentAction(() => returnToPhaseGroupConfirmation);
    };

    const generateFinalMatchesConfirmAction = async () => {
        const response = await generateFinalMatches(tournament.id);
        if (response.success) {
            setModalMessage(response.data);
            setConfirmLabel("");
            setShowConfirmButton(false);
            setIsCustomModalOpen(true);
            await fetchMatchData();
            await fetchStandingsData();
        } else {
            console.error('Failed to generate final matches:', response.data);
            setModalMessage(response.data || 'error');
            setIsCustomModalOpen(true);
        }
    };

    const returnToPhaseGroupConfirmation = async () => {
        const response = await returnToPhaseGroup(tournament.id);
        if (response.success) {
            setModalMessage(response.data);
            setConfirmLabel("");
            setShowConfirmButton(false);
            setIsCustomModalOpen(true);
            await fetchMatchData();
            await fetchStandingsData();
        } else {
            console.error('Failed to delete final matches:', response.data);
            setModalMessage(response.data || 'error');
            setIsCustomModalOpen(true);
        }
    };

    const getPodiumMatches = () => {
        if (tournament.competitionSystem === 'COMBINED') {
            return filterFinalPhaseMatches(matches);
        }
        return matches;
    };

    const filteredMatches = matches
        .filter(match => {
            return `${match.participant1LastName} ${match.participant1PartnerLastName} ${match.participant2LastName} ${match.participant2PartnerLastName}`.toLowerCase().includes(nameFilter.toLowerCase());
        });

    const renderCompetitionSystem = () => {
        switch (tournament.competitionSystem) {
            case 'ROUND_ROBIN':
                return (
                    <>
                        {standings.length > 0 &&
                            <RoundRobinStandings standings={standings} matches={matches} isArchived={isArchived}/>
                        }

                        <div className={styles.filterRow}>
                            <div className={styles.filterField}>
                                <svg
                                    className={styles.filterIcon}
                                    viewBox="0 0 24 24"
                                    width="16"
                                    height="16"
                                    aria-hidden="true"
                                    focusable="false"
                                >
                                    <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2"/>
                                    <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                                <input
                                    type="text"
                                    className={styles.filterInput}
                                    value={nameFilter}
                                    onChange={(e) => setNameFilter(e.target.value)}
                                    placeholder="Filtruj po nazwisku…"
                                    aria-label="Filtruj po nazwisku"
                                    autoComplete="off"
                                    spellCheck={false}
                                />
                                {nameFilter && (
                                    <button
                                        type="button"
                                        className={styles.filterClear}
                                        onClick={() => setNameFilter('')}
                                        aria-label="Wyczyść filtr"
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                        </div>

                        <MatchesTable
                            matches={filteredMatches}
                            onEditMatch={handleEditMatch}
                            isArchived={isArchived}
                            onOpenH2H={handleOpenH2H}
                        />
                        {currentMatch && (
                            <MatchResultModal
                                isOpen={isModalOpen}
                                onRequestClose={handleModalClose}
                                onSaveResult={onSaveResult}
                                editingMatch={currentMatch}
                                matchId={editingMatchId}
                                participant1Id={currentMatch.participant1Id}
                                participant2Id={currentMatch.participant2Id}
                                participant1FirstName={currentMatch.participant1FirstName}
                                participant1LastName={currentMatch.participant1LastName}
                                participant2FirstName={currentMatch.participant2FirstName}
                                participant2LastName={currentMatch.participant2LastName}
                                participant1PartnerFirstName={currentMatch.participant1PartnerFirstName}
                                participant1PartnerLastName={currentMatch.participant1PartnerLastName}
                                participant2PartnerFirstName={currentMatch.participant2PartnerFirstName}
                                participant2PartnerLastName={currentMatch.participant2PartnerLastName}
                                isThirdSetTiebreak={tournament.isThirdSetTiebreak}
                                matchFormat={tournament.matchFormat}
                                numberOfPointsInSet={tournament.numberOfPointsInSet}
                                isUserSupervision={isUserSuperVisor}
                                showConfirmButton={showConfirmButton}
                            />
                        )}
                    </>
                );
            case 'KNOCKOUT':
                return (
                    <>
                        <SingleEliminationBracket
                            matches={matches}
                            onEditMatch={handleEditMatch}
                            isArchived={isArchived}
                            onOpenH2H={handleOpenH2H}
                        />
                        {currentMatch && (
                            <MatchResultModal
                                isOpen={isModalOpen}
                                onRequestClose={handleModalClose}
                                onSaveResult={onSaveResult}
                                editingMatch={currentMatch}
                                matchId={editingMatchId}
                                participant1Id={currentMatch.participant1Id}
                                participant2Id={currentMatch.participant2Id}
                                participant1FirstName={currentMatch.participant1FirstName}
                                participant1LastName={currentMatch.participant1LastName}
                                participant2FirstName={currentMatch.participant2FirstName}
                                participant2LastName={currentMatch.participant2LastName}
                                participant1PartnerFirstName={currentMatch.participant1PartnerFirstName}
                                participant1PartnerLastName={currentMatch.participant1PartnerLastName}
                                participant2PartnerFirstName={currentMatch.participant2PartnerFirstName}
                                participant2PartnerLastName={currentMatch.participant2PartnerLastName}
                                isThirdSetTiebreak={tournament.isThirdSetTiebreak}
                                matchFormat={tournament.matchFormat}
                                numberOfPointsInSet={tournament.numberOfPointsInSet}
                                isUserSupervision={isUserSuperVisor}
                                showConfirmButton={showConfirmButton}
                            />
                        )}
                    </>
                );
            case 'BRAZILIAN':
            case 'BRAZILIAN_FAST':
                return (
                    <>
                        <BrazilianBracket
                            matches={matches}
                            onEditMatch={handleEditMatch}
                            isArchived={isArchived}
                            onOpenH2H={handleOpenH2H}
                            tournamentStatus={tournament.status}
                        />
                        {currentMatch && (
                            <MatchResultModal
                                isOpen={isModalOpen}
                                onRequestClose={handleModalClose}
                                onSaveResult={onSaveResult}
                                editingMatch={currentMatch}
                                matchId={editingMatchId}
                                participant1Id={currentMatch.participant1Id}
                                participant2Id={currentMatch.participant2Id}
                                participant1FirstName={currentMatch.participant1FirstName}
                                participant1LastName={currentMatch.participant1LastName}
                                participant2FirstName={currentMatch.participant2FirstName}
                                participant2LastName={currentMatch.participant2LastName}
                                participant1PartnerFirstName={currentMatch.participant1PartnerFirstName}
                                participant1PartnerLastName={currentMatch.participant1PartnerLastName}
                                participant2PartnerFirstName={currentMatch.participant2PartnerFirstName}
                                participant2PartnerLastName={currentMatch.participant2PartnerLastName}
                                isThirdSetTiebreak={tournament.isThirdSetTiebreak}
                                matchFormat={tournament.matchFormat}
                                numberOfPointsInSet={tournament.numberOfPointsInSet}
                                isUserSupervision={isUserSuperVisor}
                                showConfirmButton={showConfirmButton}
                            />
                        )}
                    </>
                );
            case 'COMBINED':
                return (
                    <>
                        {renderCombinedSystem()}
                    </>
                );
            case 'GROUP_LEAGUE':
                return (
                    <LeagueView
                        tournament={tournament}
                        isArchived={isArchived}
                        onReturn={onReturn}
                    />
                );
            default:
                return <p>Unsupported competition system</p>;
        }
    };

    if ((isArchived || tournament.isLeague) && isLoading) return <Spinner size={"large"}/>;

    return (
        <>
            <CustomModal
                isOpen={isCustomModalOpen}
                onRequestClose={handleCloseCustomModal}
                message={modalMessage}
                confirmLabel={showConfirmButton ? confirmLabel : undefined}
                onConfirm={handleConfirm}
                isPositiveMessage={modalMessage.includes("pomyślnie") || modalMessage.includes("wygenerowane") || modalMessage.includes("skasowane")}
                cancelLabel="Zamknij"
                confirmButtonColor="red"
                cancelButtonColor="green"
                showConfirmButton={showConfirmButton}
            />
            <div className={styles.container}>
                <div className={styles.backRow}>
                    <button className={styles.returnCurButton} onClick={onReturn}>
                        ← Powrót
                    </button>
                </div>
                <div className={styles.header}>
                    <div className={styles.title}>{tournament.title}</div>
                    {!isArchived && !tournament.isLeague && isLoading &&
                        <div className={styles.spinner}></div>
                    }
                </div>
                <div className={styles.metaLine}>
                    <a href="/competition-descriptions" target="_blank" rel="noreferrer"
                       className={styles.metaSystem}>
                        {competitionSystemMapping[tournament.competitionSystem]}
                    </a>
                    {!isArchived &&
                        <>
                            <span className={styles.metaDot}></span>
                            <a href="/score-rules" target="_blank" rel="noreferrer"
                               className={styles.metaLink}>Zasady gry</a>
                        </>
                    }
                </div>
                {/* Bramka na competitionSystem: planowanie meczów istnieje wyłącznie w COMBINED,
                    a liga oglądana przez ten sam komponent rysuje własne szufladki w LeagueView —
                    bez tego warunku przypadkowo ustawiona flaga dałaby dwa komplety przycisków.
                    Sam przycisk „Planowanie meczów" zostaje bramkowany przez planningContext, bo
                    o uprawnieniach decyduje backend; obie szufladki są publiczne (endpointy
                    /leagues/{id}/recent-results i /scheduled-matches są permitAll), więc gość
                    bez logowania widzi je tak samo jak w lidze. */}
                {tournament.competitionSystem === 'COMBINED' && tournament.matchPlanningEnabled && !isArchived && (
                    <>
                        {planningContext?.enabled && planningContext?.myParticipantId && (
                            <div className={styles.planningRow}>
                                <button
                                    className={styles.planningButton}
                                    onClick={() => navigate(`/tournaments/${tournament.id}/match-planning`)}
                                >
                                    🎾 Planowanie meczów
                                </button>
                            </div>
                        )}
                        <div className={styles.drawerLinksRow}>
                            <button
                                type="button"
                                className={styles.drawerLink}
                                onClick={() => setIsRecentDrawerOpen(true)}
                            >
                                🕒 Ostatnie wyniki
                            </button>
                            <button
                                type="button"
                                className={styles.drawerLink}
                                onClick={() => setIsScheduledDrawerOpen(true)}
                            >
                                📅 Zaplanowane mecze
                            </button>
                        </div>

                        <RecentResultsDrawer
                            open={isRecentDrawerOpen}
                            onClose={() => setIsRecentDrawerOpen(false)}
                            tournamentId={tournament.id}
                        />
                        <ScheduledMatchesDrawer
                            open={isScheduledDrawerOpen}
                            onClose={() => setIsScheduledDrawerOpen(false)}
                            tournamentId={tournament.id}
                        />
                    </>
                )}
                {isArchived && <Podium standings={standings} matches={getPodiumMatches()}
                                       competitionSystem={tournament.competitionSystem}
                                       leagueRankings={leagueRankings}/>}
                {renderCompetitionSystem()}
                {isH2hModalOpen && (
                    <H2HModal
                        isOpen={isH2hModalOpen}
                        onRequestClose={handleCloseH2H}
                        match={matches.find(m => m.id === h2hMatchId)}
                        h2hData={h2hData}
                        isLoading={isH2hLoading}
                    />
                )}
            </div>
        </>
    );
};

export default TourCurForm;
